import {
  FieldArray,
  arrayRemoveAll,
  arrayRemove,
  getFormSyncErrors,
  getFormValues,
} from 'redux-form'
import { Select, Label } from '../../common'
import { CREATE_FORM_NAME, CREATE_FORM_FIELDS as FIELDS } from '../../../constants/SectionConstants'
import { mapToLabelValueObjectArrayAndSort } from '../../../utils/sort'

const mapStateToProps = (state, { creating }) => {
  const currentFormValues = getFormValues(CREATE_FORM_NAME)(state) || {}
  const { fetching, data: availableTeachers = [] } = state.teacher
  const selectedTeachers = currentFormValues[FIELDS.teachers]?.filter(t => t && t.teacherId) || []
  const formSyncErrors = getFormSyncErrors(CREATE_FORM_NAME)(state)
  return {
    ...state.user.data,
    availableTeachers,
    selectedTeachers,
    fetching,
    creating,
    formSyncErrors,
  }
}

const FIELD_ID = 'createOrEditCoTeachers'

const validateTeacherSelected = val =>
  val !== undefined && val?.proId !== '' ? undefined : 'Error: Select a teacher'

const TeacherRows = props => {
  const {
    fields,
    selectedTeachers = [],
    availableTeachers = [],
    maxAllowed = 3,
    atLeastOneRequired = false,
    uneditableTeacherIndex,
    creating,
    roleOfUser,
    syncErrors,
  } = props
  const numberSelected = selectedTeachers.length
  const reachedMaxAllowed = numberSelected === maxAllowed
  // So seleciing the "Choose..." option will not show the Add option till a teacher
  // has been selected
  const sectionTeacherErrors = syncErrors?.sectionTeachers?.some(er => er !== undefined) || 0
  const showAddAnother =
    !reachedMaxAllowed &&
    numberSelected < availableTeachers.length &&
    !sectionTeacherErrors &&
    (numberSelected === fields.length ||
      (roleOfUser === 'teacher' && !creating && numberSelected === fields.length - 1) ||
      (roleOfUser === 'coordinator' && creating && numberSelected === fields.length - 1))
  const showDelete =
    !atLeastOneRequired || (atLeastOneRequired && (numberSelected > 1 || fields.length > 1))

  return (
    <>
      {fields.map((field, i) => {
        const { teacherId: currentId } = fields.get(i) || {}
        const teachersWithPreviousSelectionsRemoved = availableTeachers.filter(t => {
          return !selectedTeachers.find(s => {
            return s?.teacherId == t?.proId && s?.teacherId != currentId
          })
        })
        const options = mapToLabelValueObjectArrayAndSort(
          teachersWithPreviousSelectionsRemoved,
          t => `${t.firstName} ${t.lastName}`,
          'proId',
          ['lastName', 'firstName']
        )

        if (uneditableTeacherIndex === i) return null

        return (
          <div className="row" key={currentId || i}>
            <div
              className={showDelete ? 'col-xs-9' : 'col-xs-12'}
              style={showDelete ? { paddingRight: 5 } : {}}
            >
              <Select
                name={field}
                options={options}
                isRequired={atLeastOneRequired && i === 0}
                labelledBy={FIELD_ID}
                normalize={val => ({ teacherId: val })}
                format={val => (val ? val.proId ?? val.teacherId : '')}
                truncate={showDelete ? 17 : false}
                style={{ marginBottom: 5 }}
                validate={[validateTeacherSelected]}
              />
            </div>
            {showDelete ? (
              <div className="col-xs-3" style={{ paddingLeft: 0, lineHeight: '40px' }}>
                <button
                  type="button"
                  className="btn-link cb-small-font-size"
                  onClick={() => fields.remove(i)}
                  style={{ padding: 0 }}
                >
                  Delete
                </button>
              </div>
            ) : null}
          </div>
        )
      })}
      {showAddAnother ? (
        <button
          type="button"
          onClick={() => fields.push()}
          className="btn-link cb-small-font-size"
          style={{ border: 'none', padding: 0, display: 'block' }}
        >
          <span aria-hidden={true} className="cb-glyph cb-plus" />
          Add Co-Teacher
        </button>
      ) : null}
      {reachedMaxAllowed ? (
        <p className="cb-small-font-size">You have added the maximum number of teachers.</p>
      ) : null}
    </>
  )
}

class CoordinatorFieldArray extends Component {
  componentDidMount() {
    this.addDefaultBlankField()
  }

  componentDidUpdate() {
    this.addDefaultBlankField()
  }

  addDefaultBlankField() {
    const { fields } = this.props
    if (!fields.length) fields.push()
  }

  render() {
    const { fields, availableTeachers, selectedTeachers, creating, formSyncErrors } = this.props

    return (
      <div className="form-group">
        <Label id={FIELD_ID} label="Teachers" isRequired={true} />
        <TeacherRows
          fields={fields}
          selectedTeachers={selectedTeachers}
          availableTeachers={availableTeachers}
          atLeastOneRequired={true}
          roleOfUser="coordinator"
          creating={creating}
          syncErrors={formSyncErrors}
        />
      </div>
    )
  }
}

const TeacherFieldArray = ({
  availableTeachers,
  selectedTeachers,
  creating,
  personId,
  fields,
  formSyncErrors,
}) => {
  const availableCoTeachers = availableTeachers.filter(t => t.proId != personId)
  const selectedCoTeachers = selectedTeachers.filter(t => t.teacherId != personId)
  const currentTeacherIndex = selectedTeachers.findIndex(t => t.teacher == personId)

  return availableCoTeachers.length ? (
    <div className="form-group">
      <Label id={FIELD_ID} label="Co-teachers" />
      <TeacherRows
        fields={fields}
        selectedTeachers={selectedCoTeachers}
        availableTeachers={availableCoTeachers}
        uneditableTeacherIndex={currentTeacherIndex}
        maxAllowed={2}
        roleOfUser="teacher"
        creating={creating}
        syncErrors={formSyncErrors}
      />
    </div>
  ) : null
}

class CoTeacherSelector extends Component {
  componentDidUpdate() {
    const { fetching } = this.props

    if (!fetching) {
      this.checkAndRemoveUnauthorizedTeachers()
    }
  }

  componentWillUnmount() {
    this.props.arrayRemoveAll(CREATE_FORM_NAME, FIELDS.teachers)
  }

  checkAndRemoveUnauthorizedTeachers() {
    const { availableTeachers, selectedTeachers, arrayRemove } = this.props
    const savedTeachersNoLongerAuthorized = selectedTeachers.reduce((arr, s, i) => {
      const isTeacherInAvailableList = availableTeachers.find(t => s.teacherId == t.proId)
      return isTeacherInAvailableList ? arr : [...arr, i]
    }, [])
    savedTeachersNoLongerAuthorized.forEach(index =>
      arrayRemove(CREATE_FORM_NAME, FIELDS.teachers, index)
    )
  }

  render() {
    const { isCoordinator, availableTeachers } = this.props
    if (availableTeachers && availableTeachers.length)
      return (
        <FieldArray
          {...this.props}
          name={FIELDS.teachers}
          component={isCoordinator ? CoordinatorFieldArray : TeacherFieldArray}
        />
      )

    return null
  }
}

export default connect(mapStateToProps, { arrayRemoveAll, arrayRemove })(CoTeacherSelector)
